import React, { useState } from "react";
import { T, SERIF, FAMILY, FONT } from "../theme/tokens.js";
import { APP_NAME } from "../config.js";
import { Btn, Field, Label, Labelled, Notice, Wordmark } from "../components/ui/index.jsx";
import { useAuth } from "../auth/AuthProvider.jsx";
import { friendlyError } from "../lib/supabase.js";

/* The sign-in card. Replaces the prototype's PIN screen, which identified
   people without authenticating them. Accounts are created by Heidi, so there
   is deliberately no way to make one from here.

   A first-time person arrives with a password she read out to them. It signs
   them in and lands them on the screen that asks for one of their own, and
   nowhere else. */
export default function SignIn() {
  const { signIn, resetPassword, linkError, clearLinkError } = useAuth();
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    clearLinkError();
    setBusy(true);
    if (mode === "reset") {
      const { error } = await resetPassword(email);
      if (error) setError(friendlyError(error));
      else setSent(true);
    } else {
      const { error } = await signIn(email, password);
      if (error) setError(friendlyError(error));
    }
    setBusy(false);
  };

  return (
    <div style={{
      minHeight: "100vh", background: T.navy, display: "flex",
      alignItems: "center", justifyContent: "center", fontFamily: FAMILY, padding: 16,
    }}>
      <style>{FONT}</style>
      <div style={{
        background: T.panel, border: `1px solid ${T.line}`,
        borderTop: `4px solid ${T.gold}`, borderRadius: 6, padding: 36,
        width: "100%", maxWidth: 400,
      }}>
        <Wordmark onLight />
        <div style={{ marginTop: 12 }}><Label>{APP_NAME}</Label></div>
        <div style={{ fontSize: 13, color: T.soft, margin: "6px 0 22px" }}>
          Contract intelligence, compliance, and program tracking
        </div>

        {/* A spent or expired link says so here rather than silently putting
            the sign-in card back, which reads as nothing having happened. */}
        {linkError && !sent && (
          <div style={{ marginBottom: 14 }}>
            <Notice kind="error">{linkError}</Notice>
          </div>
        )}

        {sent ? (
          <>
            <Notice kind="ok">
              If an account exists for {email}, a reset link is on its way. The link
              expires, so use it soon.
            </Notice>
            <div style={{ fontSize: 12, color: T.soft, marginTop: 10, lineHeight: 1.6 }}>
              Check your junk folder. If nothing arrives, ask Heidi for a new
              password instead, which takes her about ten seconds and does not
              depend on mail reaching you at all.
            </div>
            <div style={{ marginTop: 14 }}>
              <Btn kind="ghost" full onClick={() => { setSent(false); setMode("signin"); }}>
                Back to sign in
              </Btn>
            </div>
          </>
        ) : (
          <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
            <Labelled label="Work email">
              <Field f={{ type: "email" }} value={email} onChange={setEmail} autoFocus
                placeholder="you@wittobriens.com" />
            </Labelled>

            {mode === "signin" && (
              <Labelled label="Password">
                <Field f={{ type: "password" }} value={password} onChange={setPassword}
                  placeholder="At least 8 characters" />
              </Labelled>
            )}

            {error && <Notice kind="error">{error}</Notice>}

            <Btn type="submit" full disabled={busy || !email || (mode === "signin" && !password)}>
              {busy ? "Working..." : mode === "signin" ? "Sign in" : "Send reset link"}
            </Btn>

            <button
              type="button"
              onClick={() => { setMode(mode === "signin" ? "reset" : "signin"); setError(""); }}
              style={{ background: "none", border: "none", color: T.soft, fontSize: 12.5, cursor: "pointer", padding: 0 }}
            >
              {mode === "signin" ? "Forgot your password?" : "Back to sign in"}
            </button>
          </form>
        )}

        <div style={{
          marginTop: 22, paddingTop: 14, borderTop: `1px solid ${T.line}`,
          fontSize: 12, color: T.soft, lineHeight: 1.5,
        }}>
          Accounts are created by Heidi, not from here. If you need access, ask her
          and she will set you up and pass you a password.
        </div>
      </div>
    </div>
  );
}
